import { useEffect, useRef, useState } from "react";

import { type OptionType } from "@vega-core/models/options";

const getInitialValue = (
  options: OptionType[] = [],
  value: string[] | string | undefined,
) => {
  if (!value) {
    return Array.isArray(value) ? [] : { label: "", value: "" };
  }

  if (Array.isArray(value)) {
    return options.filter((option) => value.includes(option.value));
  }

  const foundOption = options.find((option) => option.value === value);

  return foundOption || { label: "", value: "" };
};

type UseDropdownStateProps = {
  value?: string[] | string;
  options: OptionType[];
  onChange?: (value: OptionType) => void;
};

export const useDropdownState = ({
  value,
  options,
  onChange,
}: UseDropdownStateProps) => {
  const [focusedElement, setFocusedElement] = useState<number>();
  const [showMenu, setShowMenu] = useState(false);
  const [selectedValue, setSelectedValue] = useState<OptionType | OptionType[]>(
    getInitialValue(options, value),
  );
  const [searchValue, setSearchValue] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);

  // Update selected value when value prop or options change
  useEffect(() => {
    const newSelectedValue = getInitialValue(options, value);
    setSelectedValue(newSelectedValue);
  }, [value, options]);

  // Handle search input focus and click outside
  useEffect(() => {
    setSearchValue("");

    if (showMenu && searchRef.current) {
      searchRef.current.focus();
    }

    const handler = (e: globalThis.MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setTimeout(() => setShowMenu(false));
        setFocusedElement(undefined);
      }
    };

    window.addEventListener("click", handler, { capture: true });

    return () =>
      window.removeEventListener("click", handler, { capture: true });
  }, [showMenu]);

  const handleInputClick = () => {
    setShowMenu(!showMenu);
    setFocusedElement(undefined);
  };

  const onValueChange = (newValue: OptionType) => {
    setSelectedValue(newValue);
    onChange?.(newValue);
  };

  return {
    focusedElement,
    setFocusedElement,
    showMenu,
    selectedValue,
    searchValue,
    setSearchValue,
    searchRef,
    inputRef,
    handleInputClick,
    onValueChange,
  };
};
